import React, { createContext, useContext, useEffect } from 'react';
import { useAuthStore } from '../store/auth.store';
import type { User } from '@edytech/shared/types';
import {
  isTokenValid,
  clearTokens,
  getRefreshTokenExpiry,
  onSessionExpired,
  onTokensRefreshed,
  refreshAccessToken,
} from '../lib/api-client';
import { apiPut } from '../lib/api-client';
import type { RegisterFormData } from '@edytech/shared/validators';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (identifier: string, password: string) => Promise<void>;
  register: (data: RegisterFormData) => Promise<void>;
  socialLogin: (provider: 'google' | 'facebook', idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: { name: string; email: string; phone?: string }) => Promise<User>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    user,
    isLoading,
    isAuthenticated,
    error,
    login: storeLogin,
    register: storeRegister,
    socialLogin: storeSocialLogin,
    logout: storeLogout,
    loadProfile,
    clearSession,
    clearError,
  } = useAuthStore();

  // Check token validity on mount
  useEffect(() => {
    const initAuth = async () => {
      const initialUser = useAuthStore.getState().user;
      if (isTokenValid() || getRefreshTokenExpiry()) {
        try {
          if (!isTokenValid()) await refreshAccessToken();
          await loadProfile();
        } catch {
          clearTokens();
          clearSession();
        }
      } else if (initialUser) {
        clearSession();
      }
    };
    initAuth();

    const handleSessionExpired = () => {
      clearTokens();
      clearSession();
      window.location.href = '/?session=expired';
    };
    const removeExpiredListener = onSessionExpired(handleSessionExpired);
    let expiryTimer: number | undefined;
    const scheduleExpiry = () => {
      if (expiryTimer !== undefined) window.clearTimeout(expiryTimer);
      const expiry = getRefreshTokenExpiry();
      if (expiry) {
        expiryTimer = window.setTimeout(handleSessionExpired, Math.max(0, expiry - Date.now()));
      }
    };
    scheduleExpiry();
    const removeRefreshListener = onTokensRefreshed(scheduleExpiry);

    return () => {
      removeExpiredListener();
      removeRefreshListener();
      if (expiryTimer !== undefined) window.clearTimeout(expiryTimer);
    };
  }, [clearSession, loadProfile]);

  // Wrapper functions to match the interface expected by components
  const login = async (identifier: string, password: string) => {
    await storeLogin({ identifier, password });
  };

  const register = async (data: RegisterFormData) => {
    await storeRegister(data);
  };

  const socialLogin = async (provider: 'google' | 'facebook', idToken: string) => {
    await storeSocialLogin(provider, idToken);
  };

  const logout = async () => {
    await storeLogout();
  };

  const updateProfile = async (data: { name: string; email: string; phone?: string }): Promise<User> => {
    try {
      const response = await apiPut<{ user: User }>('/auth/profile', data);
      const updatedUser = response.user;
      // Update store
      useAuthStore.setState({ user: updatedUser });
      return updatedUser;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      await apiPut('/auth/password', { currentPassword, newPassword });
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    error,
    login,
    register,
    socialLogin,
    logout,
    updateProfile,
    changePassword,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};